import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import type React from 'react';
import Body from 'storybookConfig/stories/molecules/Body';
import DecisionButton from 'storybookConfig/stories/molecules/Button/DecisionButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Icon from 'storybookConfig/stories/molecules/Icon';
import { CommerceType } from 'types/models/company';

/* The inkwell wash's two glows, iris into signal blue, filling the icon's strokes. */
const GradientIcon = styled(Icon)`
  &::before {
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.color.irisWash},
      ${({ theme }) => theme.color.signalBlue}
    );
  }
`;

interface ContentProps {
  imageNode: React.ReactNode;
  heading: string;
  text: string;
}

const Content: React.FC<ContentProps> = ({ imageNode, heading, text }) => {
  return (
    <Flex column alignItemsCenter gap="16px">
      {imageNode}

      <Flex column gap="8px">
        <Heading variant="Headings/H4" as="h2" align="center">
          {heading}
        </Heading>

        <Body variant="Body/Body Small" color="bodyTextSecondary" align="center">
          {text}
        </Body>
      </Flex>
    </Flex>
  );
};

interface CommerceTypeDecisionButtonProps extends React.ComponentProps<typeof DecisionButton> {
  variant: CommerceType;
}

const CommerceTypeDecisionButton: React.FC<CommerceTypeDecisionButtonProps> = ({
  variant,
  ...rest
}) => {
  if (variant === CommerceType.Seller)
    return (
      <DecisionButton {...rest}>
        <Content
          imageNode={
            <Flex gap="8px" alignItemsCenter>
              <GradientIcon name="truck" size="48px" />
              <Icon name="arrow-right" size="24px" color="bodyTextCaption" />
              <GradientIcon name="store" size="48px" />
            </Flex>
          }
          heading="Selling to Retailer"
          text="You might call yourself a supplier, vendor, or wholesaler."
        />
      </DecisionButton>
    );

  if (variant === CommerceType.Buyer)
    return (
      <DecisionButton {...rest}>
        <Content
          imageNode={
            <Flex gap="8px" alignItemsCenter>
              <GradientIcon name="warehouse" size="48px" />
              <Icon name="arrow-left" size="24px" color="bodyTextCaption" />
              <GradientIcon name="shopping-basket" size="48px" />
            </Flex>
          }
          heading="Buying from Supplier"
          text="You might call yourself a retailer, distributor, or marketplace."
        />
      </DecisionButton>
    );

  return null;
};

export default CommerceTypeDecisionButton;
